import {
  faClipboardList,
  faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DistrictGradebook from 'models/analytics/DistrictGradebook';
import StudentMetrics from 'models/analytics/StudentMetrics';
import Assignment from 'models/Assignment';
import TeacherGradebook from 'models/TeacherGradebook';
import TeacherGradebookEntry from 'models/TeacherGradebookEntry';
import React from 'react';
import contentService from 'services/contentService';
import ActionButton from 'view/components/buttons/ActionButton';
import AppSidebar from 'view/components/common/Sidebar';

type AnalyticsSidebarProps = {
  onExitAnalytics: () => void;
  onAssignmentSelectChange: (event: any) => void;
  onStudentSelectChange: (event: any) => void;
  onDownloadProgressReport: () => void;
  selectedStudent?: TeacherGradebookEntry;
  selectedAssignment?: Assignment;
  gradebook?: TeacherGradebook;
  districtGradebook?: DistrictGradebook;
  studentMetrics?: StudentMetrics;
  averages?: Record<string, any>;
};

const AnalyticsSidebar: React.FC<AnalyticsSidebarProps> = ({
  onExitAnalytics,
  onAssignmentSelectChange,
  onStudentSelectChange,
  onDownloadProgressReport,
  selectedStudent,
  selectedAssignment,
  gradebook,
  districtGradebook,
  studentMetrics,
  averages,
}) => {
  const pageContent = contentService.getTeacherAnalyticsPage();

  return (
    <>
      <AppSidebar onExit={onExitAnalytics}>
        <>
          {gradebook && (
            <>
              <div className="sidebar-text">
                <FontAwesomeIcon icon={faClipboardList} />
                <label className="label-normal">
                  {pageContent.sidebar.assignments.label}
                </label>
              </div>
              <select
                value={selectedAssignment?.id ?? 'all'}
                onChange={onAssignmentSelectChange}
              >
                <option value="all" id="analytics-assignment-all">
                  {pageContent.sidebar.assignments.dropdown.default}
                </option>
                {gradebook.assignments?.map((assignment) => (
                  <option
                    value={assignment.id}
                    id={`analytics-assignment-${assignment.id}`}
                    key={`analytics-assignment-${assignment.id}`}
                  >
                    {assignment.title}
                  </option>
                ))}
              </select>
            </>
          )}
          {gradebook && (
            <>
              <div className="sidebar-text">
                <FontAwesomeIcon icon={faGraduationCap} />
                <label className="label-normal">
                  {pageContent.sidebar.students.label}
                </label>
              </div>
              <select
                value={selectedStudent?.username ?? 'all'}
                onChange={onStudentSelectChange}
              >
                <option value="all" id="analytics-student-all">
                  {pageContent.sidebar.students.dropdown.default}
                </option>
                {gradebook?.students.map((student) => (
                  <option
                    value={student.username}
                    id={`analytics-student-${student.username}`}
                    key={`analytics-student-${student.username}`}
                  >
                    {student.displayName()}
                  </option>
                ))}
              </select>
            </>
          )}
          <div className="assignment-detail-header">
            <label className="label-medium">Student benchmarks</label>
          </div>
          <div className="assignment-details-list">
            <div className="assignment-detail">
              <label className="label-small">{'ESL listening'}</label>
              <label className="label-small">
                {districtGradebook
                  ? districtGradebook.averages.wida_listening
                  : gradebook?.averages.wida_listening}
                /6
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">{'ESL reading'}</label>
              <label className="label-small">
                {districtGradebook
                  ? districtGradebook.averages.wida_reading
                  : gradebook?.averages.wida_reading}
                /6
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">{'ESL speaking'}</label>
              <label className="label-small">
                {districtGradebook
                  ? districtGradebook.averages.wida_speaking
                  : gradebook?.averages.wida_speaking}
                /6
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">{'ESL writing'}</label>
              <label className="label-small">
                {districtGradebook
                  ? districtGradebook.averages.wida_writing
                  : gradebook?.averages.wida_writing}
                /6
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">{'Reading'}</label>
              <label className="label-small">
                {districtGradebook
                  ? districtGradebook.averages.lexile_reading
                  : gradebook?.averages.lexile_reading}
                /2000
              </label>
            </div>
          </div>
          <div className="assignment-detail-header">
            <label className="label-medium">
              {gradebook ? 'Class' : 'District'} completion
            </label>
          </div>
          <div className="assignment-details-list">
            <div className="assignment-detail">
              <label className="label-small">
                {pageContent.sidebar.details.completion.attributes[0].label}
              </label>
              <label className="label-small">
                {averages?.completion_score}%
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">
                {pageContent.sidebar.details.completion.attributes[1].label}
              </label>
              <label className="label-small">{averages?.grade}%</label>
            </div>
            {/* <div className="assignment-detail">
              <label className="label-small">
                {pageContent.sidebar.details.completion.attributes[2].label}
              </label>
              <label className="label-small">
                {formatSeconds(averages?.completion_time)}
              </label>
            </div> */}
          </div>
          <div className="assignment-detail-header">
            <label className="label-medium">
              {gradebook ? 'Class' : 'District'} accuracy
            </label>
          </div>
          <div className="assignment-details-list">
            <div className="assignment-detail">
              <label className="label-small">
                {pageContent.sidebar.details.accuracy.attributes[0].label}
              </label>
              <label className="label-small">
                {averages?.correctness_score}%
              </label>
            </div>
            <div className="assignment-detail">
              <label className="label-small">
                {pageContent.sidebar.details.accuracy.attributes[1].label}
              </label>
              <label className="label-small">
                {averages?.key_word_accuracy_score}%
              </label>
            </div>
          </div>
          <ActionButton
            label="Download PDF Report"
            onClick={onDownloadProgressReport}
          />
        </>
      </AppSidebar>
    </>
  );
};

export default AnalyticsSidebar;
